import { createFileRoute } from "@tanstack/react-router";
import { LegalLayout } from "@/components/legal/LegalLayout";

export const Route = createFileRoute("/privacy-policy")({
  head: () => ({
    meta: [
      { title: "Privacy Policy — FlashWeb Agency" },
      { name: "description", content: "Informativa privacy di FlashWeb Agency (FVT FuturaVision Technology GmbH): quali dati raccogliamo, come li trattiamo e i tuoi diritti secondo GDPR e LPD svizzera." },
      { property: "og:title", content: "Privacy Policy — FlashWeb Agency" },
      { property: "og:description", content: "Come trattiamo i tuoi dati personali secondo GDPR e LPD svizzera." },
      { property: "og:url", content: "https://www.flash-web.ch/privacy-policy" },
      { name: "robots", content: "index, follow" },
    ],
    links: [{ rel: "canonical", href: "https://www.flash-web.ch/privacy-policy" }],
  }),
  component: PrivacyPage,
});

function PrivacyPage() {
  return (
    <LegalLayout title="Privacy Policy" updated="7 luglio 2026">
      <h2>1. Chi siamo</h2>
      <p>
        Il presente sito è gestito da <strong>FVT FuturaVision Technology GmbH</strong>, società di diritto
        svizzero con sede a Zug, che opera con il brand commerciale <strong>FlashWeb Agency</strong>{" "}
        (di seguito "FlashWeb", "noi").
      </p>
      <ul>
        <li>Sito: <a href="https://www.flash-web.ch">www.flash-web.ch</a></li>
        <li>Email: <a href="mailto:info@flash-web.ch">info@flash-web.ch</a></li>
        <li>Sedi operative: Bareerstrasse 75, 6300 Zug — Via Ai Prati 2, Bedano</li>
      </ul>
      <p>
        FlashWeb è il titolare del trattamento dei dati personali raccolti tramite il sito.
      </p>

      <h2>2. Quali dati raccogliamo</h2>
      <p>Raccogliamo esclusivamente i dati necessari per rispondere alle tue richieste ed erogare i nostri servizi:</p>
      <ul>
        <li><strong>Dati identificativi e di contatto</strong>: nome, cognome, azienda, email, telefono.</li>
        <li><strong>Contenuto delle comunicazioni</strong>: informazioni che ci invii tramite form, email o WhatsApp.</li>
        <li><strong>Dati tecnici</strong>: indirizzo IP, tipo di dispositivo, browser, sistema operativo, pagine visitate, referrer.</li>
        <li><strong>Dati di navigazione</strong>: raccolti tramite cookie, come descritto nella <a href="/cookie-policy">Cookie Policy</a>.</li>
      </ul>

      <h3>2.1 Dati inviati tramite form</h3>
      <p>
        Quando compili un form di preventivo, contatto o iscrizione alla newsletter, trattiamo i dati che
        ci fornisci per rispondere alla tua richiesta, inviarti offerte personalizzate e, se autorizzato,
        comunicazioni marketing.
      </p>

      <h3>2.2 Dati tecnici e analytics</h3>
      <p>
        Utilizziamo strumenti di analisi (es. Google Analytics, tag di conversione) che possono raccogliere
        dati tecnici in forma aggregata o pseudonimizzata. Questi strumenti sono attivati solo previo tuo
        consenso tramite il banner cookie.
      </p>

      <h2>3. Finalità del trattamento</h2>
      <ul>
        <li>Rispondere a richieste di preventivo e informazioni.</li>
        <li>Erogare i servizi contrattualizzati (sviluppo siti, hosting, manutenzione).</li>
        <li>Fatturazione, adempimenti contabili e fiscali.</li>
        <li>Comunicazioni di servizio e assistenza clienti.</li>
        <li>Marketing diretto, previo consenso specifico.</li>
        <li>Miglioramento del sito e dei servizi tramite analisi statistiche.</li>
        <li>Adempimento a obblighi di legge.</li>
      </ul>

      <h2>4. Base giuridica</h2>
      <ul>
        <li><strong>Esecuzione di un contratto</strong> o misure precontrattuali (art. 6.1.b GDPR).</li>
        <li><strong>Consenso</strong> per marketing, analytics e cookie non necessari (art. 6.1.a GDPR).</li>
        <li><strong>Legittimo interesse</strong> per sicurezza del sito, prevenzione abusi (art. 6.1.f GDPR).</li>
        <li><strong>Obbligo di legge</strong> per adempimenti fiscali e contabili (art. 6.1.c GDPR).</li>
      </ul>

      <h2>5. Conservazione dei dati</h2>
      <p>
        Conserviamo i dati per il tempo strettamente necessario alle finalità sopra indicate:
      </p>
      <ul>
        <li>Dati di contatto e richieste: fino a 24 mesi dall'ultimo contatto.</li>
        <li>Dati contrattuali e di fatturazione: 10 anni, per obbligo di legge svizzero.</li>
        <li>Dati marketing: fino a revoca del consenso.</li>
        <li>Log tecnici: massimo 12 mesi.</li>
      </ul>

      <h2>6. Condivisione con fornitori tecnici</h2>
      <p>
        I dati possono essere trattati, per nostro conto e nei limiti delle finalità dichiarate, da
        fornitori di servizi tecnologici che agiscono come responsabili del trattamento:
      </p>
      <ul>
        <li>Provider di hosting e infrastruttura (Cloudflare, Hostpoint, Supabase).</li>
        <li>Servizi di email transazionale e newsletter.</li>
        <li>Piattaforme di analytics e pubblicità (Google, Meta), se acconsentito.</li>
        <li>Consulenti contabili e legali.</li>
      </ul>
      <p>
        Ove i dati siano trasferiti fuori Svizzera/UE, garantiamo adeguate misure di protezione (Clausole
        Contrattuali Standard, decisioni di adeguatezza).
      </p>

      <h2>7. Diritti dell'utente</h2>
      <p>In qualsiasi momento puoi esercitare i seguenti diritti:</p>
      <ul>
        <li>Accesso, rettifica, cancellazione e portabilità dei dati.</li>
        <li>Limitazione o opposizione al trattamento.</li>
        <li>Revoca del consenso, senza pregiudicare la liceità dei trattamenti precedenti.</li>
        <li>Reclamo all'autorità di controllo competente (in Svizzera: IFPDT; in UE: Garante Privacy nazionale).</li>
      </ul>

      <h2>8. Contatto per richieste privacy</h2>
      <p>
        Per esercitare i tuoi diritti o richiedere informazioni, scrivi a{" "}
        <a href="mailto:info@flash-web.ch">info@flash-web.ch</a>. Risponderemo entro 30 giorni.
      </p>

      <h2>9. Riferimenti normativi</h2>
      <p>
        Il presente trattamento è conforme al <strong>Regolamento UE 2016/679 (GDPR)</strong>, alla{" "}
        <strong>Legge federale sulla protezione dei dati (LPD)</strong> del 25 settembre 2020 e alle
        pertinenti normative europee.
      </p>

      <h2>10. Modifiche</h2>
      <p>
        Ci riserviamo il diritto di aggiornare la presente informativa. La versione in vigore è sempre
        pubblicata su questa pagina con l'indicazione della data di ultimo aggiornamento.
      </p>
    </LegalLayout>
  );
}
